/**
 * Convex values are stricter than JSON: object keys must be non-empty
 * printable ASCII and may not start with `$` (or `_` at the top level),
 * nesting is capped at 16 levels, and arrays at 8192 elements. InstantDB's
 * `i.json()` columns hold free-form blobs (AI visibility snapshots keyed by
 * question text, for one) that break those rules.
 *
 * The smallest offending subtree is stored as `{ __json: "<serialised>" }`
 * and decoded on the way out, so callers see the original value. The Worker
 * client encodes mutation arguments, the write helpers encode documents, the
 * import transform encodes rows, and every read helper decodes.
 * Dependency-free so scripts can import it.
 */
const WRAPPER_KEY = "__json";
const MAX_DEPTH = 15;
const MAX_ARRAY = 8192;

function validKey(key: string, topLevel: boolean): boolean {
	if (key.length === 0) return false;
	if (key.startsWith("$")) return false;
	if (topLevel && key.startsWith("_")) return false;
	for (let i = 0; i < key.length; i++) {
		const code = key.charCodeAt(i);
		if (code < 32 || code > 126) return false;
	}
	return true;
}

/** True when the value cannot be stored as a Convex value as-is. */
export function needsEncoding(value: unknown, depth = 0): boolean {
	if (depth > MAX_DEPTH) return true;
	if (Array.isArray(value)) {
		if (value.length > MAX_ARRAY) return true;
		return value.some((item) => needsEncoding(item, depth + 1));
	}
	if (value && typeof value === "object") {
		for (const [key, item] of Object.entries(
			value as Record<string, unknown>,
		)) {
			if (!validKey(key, false)) return true;
			if (needsEncoding(item, depth + 1)) return true;
		}
	}
	return false;
}

export function isEncoded(value: unknown): value is { __json: string } {
	return (
		value !== null &&
		typeof value === "object" &&
		!Array.isArray(value) &&
		Object.keys(value).length === 1 &&
		typeof (value as Record<string, unknown>)[WRAPPER_KEY] === "string"
	);
}

function wrap(value: unknown) {
	return { [WRAPPER_KEY]: JSON.stringify(value) };
}

/**
 * Wrap the smallest subtrees Convex would reject, so `{ data: { snapshot:
 * { "free text": 1 } } }` keeps `data` readable and encodes only `snapshot`.
 */
export function encodeDeep(value: unknown, depth = 0): unknown {
	if (value === null || typeof value !== "object") return value;
	if (Array.isArray(value)) {
		if (value.length > MAX_ARRAY || depth >= MAX_DEPTH) return wrap(value);
		let out: unknown[] | null = null;
		value.forEach((item, index) => {
			const encoded = encodeDeep(item, depth + 1);
			if (encoded !== item) {
				out ??= [...value];
				out[index] = encoded;
			}
		});
		return out ?? value;
	}
	const record = value as Record<string, unknown>;
	if (depth >= MAX_DEPTH) return wrap(value);
	if (!Object.keys(record).every((key) => validKey(key, false))) {
		return wrap(value);
	}
	let out: Record<string, unknown> | null = null;
	for (const [key, item] of Object.entries(record)) {
		const encoded = encodeDeep(item, depth + 1);
		if (encoded !== item) {
			out ??= { ...record };
			out[key] = encoded;
		}
	}
	return out ?? value;
}

export function decodeDeep(value: unknown): unknown {
	if (value === null || typeof value !== "object") return value;
	if (isEncoded(value)) return JSON.parse(value[WRAPPER_KEY]);
	if (Array.isArray(value)) {
		let out: unknown[] | null = null;
		value.forEach((item, index) => {
			const decoded = decodeDeep(item);
			if (decoded !== item) {
				out ??= [...value];
				out[index] = decoded;
			}
		});
		return out ?? value;
	}
	const record = value as Record<string, unknown>;
	let out: Record<string, unknown> | null = null;
	for (const [key, item] of Object.entries(record)) {
		const decoded = decodeDeep(item);
		if (decoded !== item) {
			out ??= { ...record };
			out[key] = decoded;
		}
	}
	return out ?? value;
}

export const encodeField = (value: unknown) => encodeDeep(value);
export const decodeField = (value: unknown) => decodeDeep(value);

/** Encode every field of a row about to be written. */
export function encodeRow<T extends Record<string, unknown>>(row: T): T {
	return encodeDeep(row) as T;
}

/** Decode every field of a row about to leave Convex. */
export function decodeRow<T extends Record<string, unknown>>(row: T): T {
	return decodeDeep(row) as T;
}
